import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { ArrowLeft, Save, User, Mail, Phone, MapPin, Lock } from 'lucide-react';
import { apiService } from '../services/api';
import { Store, EmployeeFormData } from '../types';
import { useToast } from '../contexts/ToastContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin: 0;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #00d4aa;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid ${props => props.$hasError ? '#dc3545' : '#dee2e6'};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc3545' : '#00d4aa'};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 123, 255, 0.1)'};
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid ${props => props.$hasError ? '#dc3545' : '#dee2e6'};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc3545' : '#00d4aa'};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(0, 123, 255, 0.1)'};
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 1;
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
`;

const ModalOverlay = styled.div<{ $show: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 50;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  width: min(520px, 92vw);
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.2);
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 1rem;
`;

const Button = styled.button<{ $variant?: 'secondary' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  ${props => props.$variant === 'secondary' ? `
    background: #6c757d;
    color: white;
    &:hover:not(:disabled) { background: #545b62; }
  ` : `
    background: #00d4aa;
    color: white;
    &:hover:not(:disabled) { background: #00b894; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 64px 20px;
  color: #6c757d;
`;

interface FormInputs {
  name: string;
  email: string;
  cpf: string;
  phone?: string;
  role: 'manager' | 'cashier' | 'attendant';
  password: string;
  password_confirmation: string;
  store_id: string;
  can_add_points: boolean;
  can_redeem_rewards: boolean;
}

export const EmployeeFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id && id !== 'new');
  const { showSuccess, showError } = useToast();

  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storeSubmitting, setStoreSubmitting] = useState(false);
  const [storePlanId, setStorePlanId] = useState<number | null>(null);
  const [storeForm, setStoreForm] = useState({
    name: '',
    address: '',
    phone: '',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setError,
    setValue
  } = useForm<FormInputs>();

  const password = watch('password');

  const loadStores = useCallback(async () => {
    try {
      const response = await apiService.getStores();
      if (response.success && response.data) {
        setStores(response.data);
      }
    } catch (error) {
      showError('Erro ao carregar lojas');
    }
  }, [showError]);

  const loadEmployee = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await apiService.getEmployee(parseInt(id));
      if (response.success && response.data) {
        const employee = response.data;
        const formatCpf = (cpf: string) => cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

        reset({
          name: employee.name,
          email: employee.email,
          cpf: employee.cpf ? formatCpf(employee.cpf) : '',
          phone: employee.phone || '',
          store_id: employee.store_id?.toString() || '',
          role: employee.role || 'attendant',
          can_add_points: employee.can_add_points ?? false,
          can_redeem_rewards: employee.can_redeem_rewards ?? false,
          password: '',
          password_confirmation: ''
        });
      }
    } catch (error) {
      showError('Erro ao carregar funcionário');
    } finally {
      setLoading(false);
    }
  }, [id, reset, showError]);

  useEffect(() => {
    loadStores();
    if (isEditing) {
      loadEmployee();
    } else {
      setLoading(false);
    }
  }, [isEditing, loadEmployee, loadStores]);

  useEffect(() => {
    const loadPlan = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/dashboard/plan-usage`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        const data = await response.json();
        if (data.success && data.data?.plan?.id) {
          setStorePlanId(data.data.plan.id);
        }
      } catch (error) {
        console.error('Erro ao carregar plano para loja:', error);
      }
    };

    loadPlan();
  }, []);

  const handleCreateStore = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!storePlanId) {
      showError('Plano da empresa não encontrado.');
      return;
    }

    if (!storeForm.name.trim()) {
      showError('Nome da loja é obrigatório.');
      return;
    }

    setStoreSubmitting(true);
    try {
      const response = await apiService.createStore({
        name: storeForm.name.trim(),
        plan_id: storePlanId,
        cnpj: '',
        description: '',
        email: '',
        phone: storeForm.phone.trim() || undefined,
        address: {
          street: storeForm.address.trim() || '',
          number: '',
          neighborhood: '',
          city: '',
          state: '',
          postal_code: '',
        },
      });

      if (response.success && response.data) {
        const newStore = response.data;
        await loadStores();
        setValue('store_id', newStore.id.toString());
        setShowStoreModal(false);
        setStoreForm({ name: '', address: '', phone: '' });
        showSuccess('Loja criada com sucesso!');
      } else {
        showError(response.message || 'Erro ao criar loja.');
      }
    } catch (error) {
      console.error('Erro ao criar loja:', error);
      showError('Erro ao criar loja.');
    } finally {
      setStoreSubmitting(false);
    }
  };

  const onSubmit = async (data: FormInputs) => {
    setSubmitting(true);
    
    try {
      const employeeData: EmployeeFormData = {
        name: data.name,
        email: data.email,
        cpf: data.cpf.replace(/\D/g, ''),
        phone: data.phone || undefined,
        role: data.role as 'manager' | 'cashier' | 'attendant',
        store_id: parseInt(data.store_id),
        password: data.password,
        password_confirmation: data.password_confirmation,
        can_add_points: data.can_add_points,
        can_redeem_rewards: data.can_redeem_rewards
      };

      let response;
      if (isEditing) {
        response = await apiService.updateEmployee(parseInt(id!), employeeData);
      } else {
        response = await apiService.createEmployee(employeeData);
      }

      if (response.success) {
        showSuccess(isEditing ? 'Funcionário atualizado com sucesso!' : 'Funcionário criado com sucesso!');
        navigate('/employees');
      } else {
        // Map API validation errors (if any) to form fields
        if ((response as any).errors) {
          const errors = (response as any).errors;
          Object.keys(errors).forEach((field) => {
            setError(field as any, { type: 'server', message: errors[field][0] });
          });
        } else {
          showError('Erro ao salvar funcionário');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar funcionário:', error);
      // Try to extract validation errors from Axios 422
      const anyErr = error as any;
      const responseData = anyErr?.response?.data;
      if (responseData?.errors) {
        Object.keys(responseData.errors).forEach((field) => {
          setError(field as any, { type: 'server', message: responseData.errors[field][0] });
        });
      } else if (responseData?.message) {
        showError(responseData.message);
      } else {
        showError('Erro ao salvar funcionário');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Carregando...</LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate('/employees')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <PageTitle>
          {isEditing ? 'Editar Funcionário' : 'Novo Funcionário'}
        </PageTitle>
      </Header>

      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label>Nome</Label>
            <InputContainer>
              <InputIcon>
                <User size={16} />
              </InputIcon>
              <Input
                type="text"
                placeholder="Nome completo"
                $hasError={!!errors.name}
                {...register('name', {
                  required: 'Nome é obrigatório',
                  minLength: {
                    value: 2,
                    message: 'Nome deve ter pelo menos 2 caracteres'
                  }
                })}
              />
            </InputContainer>
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Email</Label>
            <InputContainer>
              <InputIcon>
                <Mail size={16} />
              </InputIcon>
              <Input
                type="email"
                placeholder="email@exemplo.com"
                $hasError={!!errors.email}
                {...register('email', {
                  required: 'Email é obrigatório',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido'
                  }
                })}
              />
            </InputContainer>
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>CPF</Label>
            <InputContainer>
              <InputIcon>
                <User size={16} />
              </InputIcon>
              <Input
                type="text"
                placeholder="000.000.000-00"
                maxLength={14}
                $hasError={!!errors.cpf}
                {...register('cpf', {
                  required: 'CPF é obrigatório',
                  pattern: {
                    value: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
                    message: 'CPF inválido (use formato: 000.000.000-00)'
                  }
                })}
                onChange={(e) => {
                  const numbers = e.target.value.replace(/\D/g, '');
                  let formatted = '';
                  if (numbers.length <= 3) formatted = numbers;
                  else if (numbers.length <= 6) formatted = `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
                  else if (numbers.length <= 9) formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
                  else formatted = `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
                  e.target.value = formatted;
                }}
              />
            </InputContainer>
            {errors.cpf && <ErrorMessage>{errors.cpf.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Telefone (Opcional)</Label>
            <InputContainer>
              <InputIcon>
                <Phone size={16} />
              </InputIcon>
              <Input
                type="tel"
                placeholder="(00) 00000-0000"
                maxLength={15}
                {...register('phone')}
                onChange={(e) => {
                  const numbers = e.target.value.replace(/\D/g, '');
                  let formatted = '';
                  if (numbers.length <= 2) formatted = numbers;
                  else if (numbers.length <= 7) formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
                  else formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
                  e.target.value = formatted;
                }}
              />
            </InputContainer>
          </FormGroup>

          <FormGroup>
            <Label>Loja</Label>
            <InlineRow>
              <span>Selecione a loja do funcionário</span>
              <LinkButton type="button" onClick={() => setShowStoreModal(true)}>
                Cadastrar nova loja
              </LinkButton>
            </InlineRow>
            <InputContainer>
              <InputIcon>
                <MapPin size={16} />
              </InputIcon>
              <Select
                $hasError={!!errors.store_id}
                {...register('store_id', {
                  required: 'Loja é obrigatória'
                })}
              >
                <option value="">Selecione uma loja</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </Select>
            </InputContainer>
            {stores.length === 0 && (
              <ErrorMessage>Cadastre uma loja antes de criar o funcionário.</ErrorMessage>
            )}
            {errors.store_id && <ErrorMessage>{errors.store_id.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>{isEditing ? 'Nova Senha (deixe em branco para manter a atual)' : 'Senha'}</Label>
            <InputContainer>
              <InputIcon>
                <Lock size={16} />
              </InputIcon>
              <Input
                type="password"
                placeholder="Digite a senha"
                $hasError={!!errors.password}
                {...register('password', isEditing ? {} : {
                  required: 'Senha é obrigatória',
                  minLength: {
                    value: 6,
                    message: 'Senha deve ter pelo menos 6 caracteres'
                  }
                })}
              />
            </InputContainer>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>

          {(!isEditing || password) && (
            <FormGroup>
              <Label>Confirmar Senha</Label>
              <InputContainer>
                <InputIcon>
                  <Lock size={16} />
                </InputIcon>
                <Input
                  type="password"
                  placeholder="Confirme a senha"
                  $hasError={!!errors.password_confirmation}
                  {...register('password_confirmation', {
                    required: (!isEditing || password) ? 'Confirmação de senha é obrigatória' : false,
                    validate: (value) => {
                      if ((!isEditing || password) && value !== password) {
                        return 'Senhas não coincidem';
                      }
                      return true;
                    }
                  })}
                />
              </InputContainer>
              {errors.password_confirmation && (
                <ErrorMessage>{errors.password_confirmation.message}</ErrorMessage>
              )}
            </FormGroup>
          )}

          <ButtonGroup>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => navigate('/employees')}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              <Save size={16} />
              {submitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar Funcionário')}
            </Button>
          </ButtonGroup>
        </Form>
      </FormContainer>

      <ModalOverlay $show={showStoreModal} onClick={() => setShowStoreModal(false)}>
        <ModalContent onClick={(event) => event.stopPropagation()}>
          <ModalTitle>Nova Loja</ModalTitle>
          <ModalSubtitle>Cadastre a loja e vincule ao funcionário</ModalSubtitle>
          <Form onSubmit={handleCreateStore}>
            <FormGroup>
              <Label>Nome da Loja</Label>
              <Input
                type="text"
                value={storeForm.name}
                onChange={(event) => setStoreForm({ ...storeForm, name: event.target.value })}
                placeholder="Ex: Loja Centro"
              />
            </FormGroup>
            <FormGroup>
              <Label>Endereço</Label>
              <Input
                type="text"
                value={storeForm.address}
                onChange={(event) => setStoreForm({ ...storeForm, address: event.target.value })}
                placeholder="Rua, número, bairro"
              />
            </FormGroup>
            <FormGroup>
              <Label>Telefone</Label>
              <Input
                type="text"
                value={storeForm.phone}
                onChange={(event) => {
                  const numbers = event.target.value.replace(/\D/g, '');
                  let formatted = '';
                  if (numbers.length <= 2) formatted = numbers;
                  else if (numbers.length <= 7) formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
                  else formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
                  setStoreForm({ ...storeForm, phone: formatted });
                }}
                placeholder="(00) 00000-0000"
              />
            </FormGroup>
            <ButtonGroup>
              <Button type="button" $variant="secondary" onClick={() => setShowStoreModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={storeSubmitting}>
                {storeSubmitting ? 'Salvando...' : 'Criar Loja'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </ModalOverlay>
    </PageContainer>
  );
};
